// ---------------------------------------------------------------------------
// The single status chip a lead row shows — and its precedence
//
// The Status column shows ONE chip: the furthest / most operative state the
// lead has reached (the rationale for one chip rather than three lives on
// `leadStateChip` in campaign-detail.ts). This module holds the ORDER and the
// pure predicates, extracted so the precedence can be tested directly rather
// than only by rendering the page.
//
// PRECEDENCE, 20 August: opt-out now outranks replied. A lead can legitimately
// carry both facts — they live in different places (`status`/`repliedAt` for
// engagement, the global Do Not Contact list for suppression) and both can be
// true at once. When they are, the operative one wins the chip: "will not be
// contacted" dominates "replied". The list route already sets
// `status:'opted_out'` + `suppressed:true` for a suppressed lead
// (routes/campaign-leads.ts), but `repliedAt` stays set, and the chip used to
// rank `replied` first — so a lead who replied AND opted out showed "replied",
// contradicting the Do Not Contact banner above the same table. The reply is
// not lost: it stays in the lead's Engagement/Messages detail and in the
// Replied filter (which still matches on `repliedAt`).
//
// LOOK, 27 October (WP27d): a state is shown the way the design system shows
// a campaign's status — a dot in its tone and a word (`toneDot` in
// campaign-status.ts) — instead of a filled badge with an icon. The icons were
// assembled from their names, which Tailwind only emits when the name is also
// written whole somewhere else (see icons-whole.test.ts).
// ---------------------------------------------------------------------------

import type { StatusTone } from './campaign-status.js';

/** The lead fields the pure chip predicates read. A subset of the page's LeadData. */
export interface LeadStateChipInput {
  status?: string;
  /**
   * Operator's Manual Overwrite. When set it WINS the chip — the whole point of
   * a manual override — except a global Do-Not-Contact suppression, which is a
   * hard safety fact and still shows. See resolveLeadStateChip.
   */
  manualStatus?: string;
  repliedAt?: string;
  bouncedAt?: string;
  /** On the global Do Not Contact list (routes/campaign-leads.ts sets this). */
  suppressed?: boolean;
  /**
   * On the global pre-existing-connection list — already a connection, or an
   * outstanding invitation, before any campaign. Shows the "skipped — already
   * connected" chip even for a pure email lead that has no LinkedIn observation
   * of its own to derive it from. Unlike `suppressed` it does NOT rewrite status.
   */
  knownConnection?: boolean;
  dmSentAt?: string;
  acceptedAt?: string;
  alreadyConnected?: string;
  invitePendingElsewhere?: string;
  connectionSentAt?: string;
  /** From the list route: this lead's connect step is still queued (WP49). */
  connectQueued?: boolean;
  connectionState?: string;
  doneAt?: string;
}

export type LeadStateChipKey =
  | 'optedOut'
  | 'replied'
  | 'bounced'
  | 'done'
  | 'dmSent'
  | 'accepted'
  | 'skippedAlreadyConnected'
  | 'alreadyConnected'
  | 'inviteSentIgnored'
  | 'inviteReceived'
  | 'connSent'
  | 'invitePending'
  | 'noInvite'
  | 'connectionUnknown'
  | 'contacted'
  | 'pending';

export interface LeadStateChip {
  key: LeadStateChipKey;
  label: string;
  /** The dot's tone: positive = good news, caution = needs a look, critical = failed, info = sent and in flight. */
  tone: StatusTone;
  /** Finished: the dot is a ring and the word is muted, like a completed campaign. */
  hollow?: boolean;
}

/**
 * Pure predicates for the chip keys that depend only on the lead's own fields.
 * `done` and `skippedAlreadyConnected` are NOT here: they need context the row
 * knows and the lead does not (whether a draft was sent; whether the campaign
 * has a DM step), so `resolveLeadStateChip` takes them as booleans instead.
 *
 * These are the SAME predicates the filter dropdown uses — campaign-detail.ts
 * imports this map for those buckets, so a chip and its bucket can never
 * disagree.
 */
export const chipPredicates: Record<
  Exclude<LeadStateChipKey, 'done' | 'skippedAlreadyConnected'>,
  (l: LeadStateChipInput) => boolean
> = {
  // A lead suppressed globally is opted out even if THIS row's status was left
  // 'replied' by an answer here — the opt-out was processed on another row.
  // A hand-set stop counts too: the LinkedIn sync rewrites `status` without
  // looking at `manualStatus` (T5 round 3), so the override is the fact.
  optedOut: l => l.status === 'opted_out' || !!l.suppressed || l.manualStatus === 'opted_out',
  // The server's leadHasReplied, exactly: the card and this filter must agree.
  replied: l => !!l.repliedAt || l.status === 'replied' || l.manualStatus === 'replied',
  bounced: l => !!l.bouncedAt || l.status === 'bounced' || l.manualStatus === 'bounced',
  dmSent: l => !!l.dmSentAt,
  accepted: l => !!l.acceptedAt,
  alreadyConnected: l => !!l.alreadyConnected,
  inviteSentIgnored: l => l.connectionState === 'invite_sent_ignored',
  inviteReceived: l => l.connectionState === 'invite_received_pending',
  connSent: l => !!l.connectionSentAt && !l.alreadyConnected && !l.invitePendingElsewhere,
  invitePending: l => !!l.invitePendingElsewhere && !l.acceptedAt && !l.alreadyConnected,
  // Not while the lead's own connect step is still queued (WP49): "no
  // invitation outstanding" is then true and says nothing — it filled the card
  // with not-yet-invited leads on 27 October 2026. A lead with nothing queued
  // and nothing outstanding is stuck, and that is what this is for.
  noInvite: l => l.connectionState === 'not_connected_no_invite' && !l.connectQueued,
  connectionUnknown: l => l.connectionState === 'unknown',
  contacted: l => l.status === 'contacted',
  pending: l => l.status === 'pending',
};

/**
 * The chips in priority order — first match wins.
 *
 * Terminal outcomes first (they override everything behind them), with opt-out
 * ahead of replied and bounced: it is the strongest "do not contact this
 * person again" signal and must not be masked by an earlier reply. Then
 * progress through the sequence, furthest first.
 */
export const LEAD_STATE_CHIPS: LeadStateChip[] = [
  { key: 'optedOut', label: 'opted out', tone: 'caution' },
  { key: 'replied', label: 'replied', tone: 'positive' },
  { key: 'bounced', label: 'bounced', tone: 'critical' },
  // Ahead of `done` (WP47): an ignored invitation is now what FINISHES a
  // LinkedIn-only lead, and "done" alone would hide why the sequence ended —
  // the one thing an operator reads this chip for.
  { key: 'inviteSentIgnored', label: 'skipped — invite ignored', tone: 'caution' },
  // "sequence finished", not "done" (T5): done read like success, and it means
  // the sequence is over — the same words as the campaign's progress line.
  { key: 'done', label: 'sequence finished', tone: 'neutral', hollow: true },
  { key: 'dmSent', label: 'DM sent', tone: 'info' },
  { key: 'accepted', label: 'accepted', tone: 'positive' },
  { key: 'skippedAlreadyConnected', label: 'skipped — already connected', tone: 'caution' },
  { key: 'alreadyConnected', label: 'already connected', tone: 'neutral' },
  { key: 'inviteReceived', label: 'they invited us', tone: 'positive' },
  // Ahead of "invite sent": an invitation we sent that is gone is not one in
  // flight (WP49 review).
  { key: 'noInvite', label: 'no invite', tone: 'neutral' },
  { key: 'connSent', label: 'invite sent', tone: 'info' },
  { key: 'invitePending', label: 'invite pending', tone: 'caution' },
  { key: 'connectionUnknown', label: 'state unknown', tone: 'critical' },
  { key: 'contacted', label: 'contacted', tone: 'info' },
  { key: 'pending', label: 'pending', tone: 'neutral' },
];

/** A manual override value → the chip key it displays as. */
const MANUAL_STATUS_CHIP_KEY: Record<string, LeadStateChipKey> = {
  pending: 'pending',
  contacted: 'contacted',
  replied: 'replied',
  bounced: 'bounced',
  opted_out: 'optedOut',
  // The operator closing a lead they do not intend to message. Added 23
  // September 2026: an already-connected lead had no way to be finished, and
  // the nearest available label was "opted out" — which is not true of them
  // and puts their address on the global Do Not Contact list for every
  // campaign. A lead nobody wants to DM is not a lead who refused contact.
  done: 'done',
};

const CHIP_BY_KEY = new Map<LeadStateChipKey, LeadStateChip>(LEAD_STATE_CHIPS.map(c => [c.key, c]));

/**
 * The chip a lead row should show, or `null` for a status this list does not
 * model (the caller renders that as itself rather than as nothing).
 *
 * A global Do-Not-Contact suppression outranks everything, including a manual
 * override — it is a hard safety fact and must never be masked (the same reason
 * opt-out outranks replied). A TERMINAL manual override (opted out/replied/
 * bounced) then wins over every computed state. A SOFT manual label (pending/
 * contacted) wins over the computed states too — surviving a leftover timestamp
 * — EXCEPT "skipped — already connected", which shows over it: the row's warning
 * already says nothing was auto-sent, and the Status chip must agree.
 *
 * `isCompleted` and `isSkippedAlreadyConnected` are computed by the row, which
 * knows the campaign's steps and the drafts; everything else is read from the
 * lead directly through `chipPredicates`.
 */
export function resolveLeadStateChip(
  lead: LeadStateChipInput,
  opts: { isCompleted: boolean; isSkippedAlreadyConnected: boolean },
): LeadStateChip | null {
  // Global Do Not Contact is a hard safety fact — it shows even over a manual label.
  if (lead.suppressed) return CHIP_BY_KEY.get('optedOut') ?? null;

  const manualKey = lead.manualStatus ? MANUAL_STATUS_CHIP_KEY[lead.manualStatus] : undefined;
  const manualChip = manualKey ? CHIP_BY_KEY.get(manualKey) : undefined;

  // A TERMINAL manual override (opted out / replied / bounced / done) is the
  // operator saying "stop this lead", and still wins over every computed state.
  // `done` MUST be in here rather than among the soft labels: an
  // already-connected lead shows "skipped — already connected" over every soft
  // label, so a soft `done` would change nothing on screen and the operator
  // could never close the lead.
  const manualIsTerminal =
    manualKey === 'optedOut' || manualKey === 'replied' || manualKey === 'bounced' || manualKey === 'done';
  if (manualChip && manualIsTerminal) return manualChip;

  // But a SOFT manual label (pending / contacted) does not mask "already
  // connected, nothing was auto-sent": the warning on the row already says it,
  // and the Status chip must agree rather than read a plain "pending". Only over
  // the manual label — the loop below keeps replied/bounced/done AHEAD of it for
  // a lead with no override, so this cannot hide a reply on an unmarked lead.
  const skippedAlreadyConnected = opts.isSkippedAlreadyConnected || !!lead.knownConnection;
  if (manualChip && skippedAlreadyConnected) return CHIP_BY_KEY.get('skippedAlreadyConnected') ?? null;

  // Otherwise the soft manual label wins over the rest of the computed states,
  // so the operator's override survives a leftover timestamp.
  if (manualChip) return manualChip;

  for (const chip of LEAD_STATE_CHIPS) {
    const matches =
      chip.key === 'done'
        ? opts.isCompleted || !!lead.doneAt
        : chip.key === 'skippedAlreadyConnected'
          ? skippedAlreadyConnected
          : chipPredicates[chip.key](lead);
    if (matches) return chip;
  }
  return null;
}
